import { useEffect, lazy, Suspense } from "react";
import ErrorBoundary from "./components/ErrorBoundary";
import UpdateBanner from "./components/UpdateBanner";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { SecurityProvider } from "./context/SecurityContext";
import { hasModule, firstAllowedPath, planLocked } from "./lib/modules";
import { Toaster } from "sonner";
import Login from "./pages/Login";
import Layout from "./components/Layout";
import ModuleLocked from "./components/ModuleLocked";
const Licenses = lazy(() => import("./pages/Licenses"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const POS = lazy(() => import("./pages/POS"));
const CustomerDisplay = lazy(() => import("./pages/CustomerDisplay"));
const Caja = lazy(() => import("./pages/Caja"));
const Inventory = lazy(() => import("./pages/Inventory"));
const Batches = lazy(() => import("./pages/Batches"));
const Customers = lazy(() => import("./pages/Customers"));
const BankCash = lazy(() => import("./pages/BankCash"));
const SalesHistory = lazy(() => import("./pages/SalesHistory"));
const Returns = lazy(() => import("./pages/Returns"));
const Purchases = lazy(() => import("./pages/Purchases"));
const Gastos = lazy(() => import("./pages/Gastos"));
const Nomina = lazy(() => import("./pages/Nomina"));
const CajasAdmin = lazy(() => import("./pages/CajasAdmin"));
const Finanzas = lazy(() => import("./pages/Finanzas"));
const Envios = lazy(() => import("./pages/Envios"));
const Multiempresa = lazy(() => import("./pages/Multiempresa"));
const Cotizaciones = lazy(() => import("./pages/Cotizaciones"));
const WhatsAppSales = lazy(() => import("./pages/WhatsAppSales"));
const Presupuestos = lazy(() => import("./pages/Presupuestos"));
const Reportes = lazy(() => import("./pages/Reportes"));
const Tareas = lazy(() => import("./pages/Tareas"));
const Checador = lazy(() => import("./pages/Checador"));
const Promociones = lazy(() => import("./pages/Promociones"));
const Users = lazy(() => import("./pages/Users"));
const Settings = lazy(() => import("./pages/Settings"));
const Asistencia = lazy(() => import("./pages/Asistencia"));
const Shopify = lazy(() => import("./pages/Shopify"));
const MiPlan = lazy(() => import("./pages/MiPlan"));
const Timbres = lazy(() => import("./pages/Timbres"));
const Tarjetas = lazy(() => import("./pages/Tarjetas"));
const SaasTimbres = lazy(() => import("./pages/SaasTimbres"));
const SaasPlans = lazy(() => import("./pages/SaasPlans"));
const AsistenciaKiosk = lazy(() => import("./pages/AsistenciaKiosk"));

function Loading() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="text-emerald-400 font-display text-xl animate-pulse">ProfesionalPOS…</div>
    </div>
  );
}

function Protected({ children, superOnly, mod }) {
  const { user, loading } = useAuth();
  const loc = useLocation();
  if (loading || user === null) return <Loading />;
  if (!user) return <Navigate to="/login" state={{ from: loc }} replace />;
  if (superOnly && user.role !== "superadmin") return <Navigate to={firstAllowedPath(user)} replace />;
  if (!superOnly && user.role === "superadmin") return <Navigate to="/licenses" replace />;
  if (mod && planLocked(user, mod)) return <Layout><ModuleLocked mod={mod} /></Layout>;
  if (mod && !hasModule(user, mod)) return <Navigate to={firstAllowedPath(user)} replace />;
  return children;
}

function AppRoutes() {
  return (
    <Suspense fallback={<Loading />}>
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/pantalla-cliente" element={<CustomerDisplay />} />
      <Route path="/checador-asistencia/:bid" element={<AsistenciaKiosk />} />
      <Route path="/asistencia" element={<Protected mod="asistencia"><Layout><Asistencia /></Layout></Protected>} />
      <Route path="/shopify" element={<Protected mod="shopify"><Layout><Shopify /></Layout></Protected>} />
      <Route path="/licenses" element={<Protected superOnly><Layout><Licenses /></Layout></Protected>} />
      <Route path="/saas/planes" element={<Protected superOnly><Layout><SaasPlans /></Layout></Protected>} />
      <Route path="/miplan" element={<Protected><Layout><MiPlan /></Layout></Protected>} />
      <Route path="/timbres" element={<Protected mod="timbres"><Layout><Timbres /></Layout></Protected>} />
      <Route path="/tarjetas" element={<Protected mod="tarjetas"><Layout><Tarjetas /></Layout></Protected>} />
      <Route path="/saas/timbres" element={<Protected superOnly><Layout><SaasTimbres /></Layout></Protected>} />
      <Route path="/dashboard" element={<Protected mod="dashboard"><Layout><Dashboard /></Layout></Protected>} />
      <Route path="/pos" element={<Protected mod="pos"><Layout><POS /></Layout></Protected>} />
      <Route path="/caja" element={<Protected mod="caja"><Layout><Caja /></Layout></Protected>} />
      <Route path="/inventory" element={<Protected mod="inventory"><Layout><Inventory /></Layout></Protected>} />
      <Route path="/batches" element={<Protected mod="batches"><Layout><Batches /></Layout></Protected>} />
      <Route path="/customers" element={<Protected mod="customers"><Layout><Customers /></Layout></Protected>} />
      <Route path="/bank" element={<Protected mod="bank"><Layout><BankCash /></Layout></Protected>} />
      <Route path="/sales" element={<Protected mod="sales"><Layout><SalesHistory /></Layout></Protected>} />
      <Route path="/returns" element={<Protected mod="returns"><Layout><Returns /></Layout></Protected>} />
      <Route path="/purchases" element={<Protected mod="purchases"><Layout><Purchases /></Layout></Protected>} />
      <Route path="/gastos" element={<Protected mod="gastos"><Layout><Gastos /></Layout></Protected>} />
      <Route path="/nomina" element={<Protected mod="nomina"><Layout><Nomina /></Layout></Protected>} />
      <Route path="/cajas" element={<Protected mod="cajas"><Layout><CajasAdmin /></Layout></Protected>} />
      <Route path="/finanzas" element={<Protected mod="finanzas"><Layout><Finanzas /></Layout></Protected>} />
      <Route path="/envios" element={<Protected mod="envios"><Layout><Envios /></Layout></Protected>} />
      <Route path="/multiempresa" element={<Protected mod="multiempresa"><Layout><Multiempresa /></Layout></Protected>} />
      <Route path="/cotizaciones" element={<Protected mod="cotizaciones"><Layout><Cotizaciones /></Layout></Protected>} />
      <Route path="/whatsapp" element={<Protected mod="whatsapp"><Layout><WhatsAppSales /></Layout></Protected>} />
      <Route path="/facturacion" element={<Navigate to="/timbres" replace />} />
      <Route path="/presupuestos" element={<Protected mod="presupuestos"><Layout><Presupuestos /></Layout></Protected>} />
      <Route path="/reportes" element={<Protected mod="reportes"><Layout><Reportes /></Layout></Protected>} />
      <Route path="/tareas" element={<Protected mod="tareas"><Layout><Tareas /></Layout></Protected>} />
      <Route path="/checador" element={<Protected mod="checador"><Checador /></Protected>} />
      <Route path="/promociones" element={<Protected mod="promociones"><Layout><Promociones /></Layout></Protected>} />
      <Route path="/users" element={<Protected mod="users"><Layout><Users /></Layout></Protected>} />
      <Route path="/settings" element={<Protected mod="settings"><Layout><Settings /></Layout></Protected>} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <ErrorBoundary>
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="top-right" theme="dark" richColors />
        <UpdateBanner />
        <SecurityProvider>
          <AppRoutes />
        </SecurityProvider>
      </BrowserRouter>
    </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
